import React, { useState } from "react";
import { Box, Typography, Card, CardContent, Switch, FormControlLabel, Select, MenuItem, FormControl, InputLabel, Chip, Divider, Alert } from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import StorageIcon from "@mui/icons-material/Storage";
import PublicIcon from "@mui/icons-material/Public";
import CustomButton from "../../components/Button/CustomButton";
import SaveIcon from "@mui/icons-material/Save";

const PrivacySettings = () => {
  const [settings, setSettings] = useState({
    hipaaCompliance: true,
    recordingFormat: "WAV",
    dataRetention: "90",
    piiDetection: true,
    piiRedaction: false,
    geoRestriction: "None",
    endToEndEncryption: true,
    recordingStorage: "US",
    transcriptRetention: "365",
  });

  const handleChange = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  const handleSave = () => {
    alert("Privacy settings saved successfully!");
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight={600}>
            Privacy & Compliance Settings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Configure HIPAA compliance and data handling policies
          </Typography>
        </Box>
        <CustomButton
          icon={<SaveIcon />}
          iconPosition="left"
          text="Save Settings"
          onClick={handleSave}
        />
      </Box>

      {settings.hipaaCompliance && (
        <Alert severity="success" sx={{ mb: 3 }}>
          HIPAA Compliance Mode is enabled. All calls and data are encrypted and stored securely.
        </Alert>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Card sx={{ borderRadius: 2, border: "1px solid #E0E0E0" }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <SecurityIcon sx={{ color: "primary.main" }} />
              <Typography variant="h6" fontWeight={600}>
                Compliance & Security
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.hipaaCompliance}
                    onChange={(e) => handleChange("hipaaCompliance", e.target.checked)}
                    color="success"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      HIPAA Compliance Mode
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Enable HIPAA-compliant data handling and secure storage
                    </Typography>
                  </Box>
                }
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.endToEndEncryption}
                    onChange={(e) => handleChange("endToEndEncryption", e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      End-to-End Encryption
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Encrypt all voice data during transmission
                    </Typography>
                  </Box>
                }
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.piiDetection}
                    onChange={(e) => handleChange("piiDetection", e.target.checked)}
                    color="warning"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      PII Detection
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Automatically detect personally identifiable information
                    </Typography>
                  </Box>
                }
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.piiRedaction}
                    onChange={(e) => handleChange("piiRedaction", e.target.checked)}
                    disabled={!settings.piiDetection}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      PII Redaction
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Automatically redact PII from transcripts and logs
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 2, border: "1px solid #E0E0E0" }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <RecordVoiceOverIcon sx={{ color: "primary.main" }} />
              <Typography variant="h6" fontWeight={600}>
                Recording Settings
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Recording Format</InputLabel>
                <Select
                  value={settings.recordingFormat}
                  label="Recording Format"
                  onChange={(e) => handleChange("recordingFormat", e.target.value)}
                >
                  <MenuItem value="WAV">
                    <Box>
                      <Typography variant="body2">WAV (Uncompressed)</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Higher quality, larger file size
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="MP3">
                    <Box>
                      <Typography variant="body2">MP3 (Compressed)</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Lower quality, smaller file size
                      </Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Recording Storage Region</InputLabel>
                <Select
                  value={settings.recordingStorage}
                  label="Recording Storage Region"
                  onChange={(e) => handleChange("recordingStorage", e.target.value)}
                >
                  <MenuItem value="US">United States</MenuItem>
                  <MenuItem value="EU">European Union</MenuItem>
                  <MenuItem value="UK">United Kingdom</MenuItem>
                  <MenuItem value="CA">Canada</MenuItem>
                  <MenuItem value="AU">Australia</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 2, border: "1px solid #E0E0E0" }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <StorageIcon sx={{ color: "primary.main" }} />
              <Typography variant="h6" fontWeight={600}>
                Data Retention
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Call Recording Retention</InputLabel>
                <Select
                  value={settings.dataRetention}
                  label="Call Recording Retention"
                  onChange={(e) => handleChange("dataRetention", e.target.value)}
                >
                  <MenuItem value="30">30 Days</MenuItem>
                  <MenuItem value="60">60 Days</MenuItem>
                  <MenuItem value="90">90 Days</MenuItem>
                  <MenuItem value="180">180 Days</MenuItem>
                  <MenuItem value="365">1 Year</MenuItem>
                  <MenuItem value="730">2 Years</MenuItem>
                  <MenuItem value="-1">Indefinite</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Transcript Retention</InputLabel>
                <Select
                  value={settings.transcriptRetention}
                  label="Transcript Retention"
                  onChange={(e) => handleChange("transcriptRetention", e.target.value)}
                >
                  <MenuItem value="30">30 Days</MenuItem>
                  <MenuItem value="60">60 Days</MenuItem>
                  <MenuItem value="90">90 Days</MenuItem>
                  <MenuItem value="180">180 Days</MenuItem>
                  <MenuItem value="365">1 Year</MenuItem>
                  <MenuItem value="730">2 Years</MenuItem>
                  <MenuItem value="-1">Indefinite</MenuItem>
                </Select>
              </FormControl>

              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  After the retention period, data will be permanently deleted and cannot be recovered.
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 2, border: "1px solid #E0E0E0" }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <PublicIcon sx={{ color: "primary.main" }} />
              <Typography variant="h6" fontWeight={600}>
                Geographic Restrictions
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Geographic Restriction</InputLabel>
                <Select
                  value={settings.geoRestriction}
                  label="Geographic Restriction"
                  onChange={(e) => handleChange("geoRestriction", e.target.value)}
                >
                  <MenuItem value="None">
                    <Box>
                      <Typography variant="body2">No Restrictions</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Allow calls from anywhere
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="US-Only">
                    <Box>
                      <Typography variant="body2">US Only</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Only allow calls from United States
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="EU-Only">
                    <Box>
                      <Typography variant="body2">EU Only</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Only allow calls from European Union
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="Custom">
                    <Box>
                      <Typography variant="body2">Custom</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Configure custom geographic restrictions
                      </Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              {settings.geoRestriction !== "None" && (
                <Box>
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    Active Restrictions:
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    <Chip
                      label={settings.geoRestriction}
                      size="small"
                      color="primary"
                      sx={{ fontWeight: 500 }}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <CustomButton
            icon={<SaveIcon />}
            iconPosition="left"
            text="Save All Settings"
            onClick={handleSave}
            size="large"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PrivacySettings;
